import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
    PlusIcon,
    NoSymbolIcon,
    MagnifyingGlassIcon,
    ExclamationTriangleIcon,
    XCircleIcon,
    CheckCircleIcon,
} from "@heroicons/react/24/outline";
import api from "../../api";
import Table, { PageControler } from "../../components/Table";
import PatientListMobile from "./PatientListMobile";
import { useMediaQuery } from "../../hooks/useMediaQuery";
import { useSystemConfig } from "../../context/systemConfigContext";
import SidebarToggleButton from "../../components/layout/SidebarToggleButton";
import PatientModuleLayout from "../../components/patients/layout/PatientModuleLayout";
import { formatDateDDMMYYYY } from "../../util/formatDate";

const columns = [
    {
        name: "name",
        displayText: "Nombre y Apellido",
        clickable: true,
        truncate: true,
    },
    { name: "govId", displayText: "C.I." },
    { name: "age", displayText: "Edad" },
    { name: "sex", displayText: "Sexo" },
    { name: "city", displayText: "Ciudad" },
    { name: "lastConsultationDate", displayText: "Última visita" },
];

const PAGE_SIZE = 20;

export default function PatientPage() {
    // eslint-disable-next-line no-unused-vars
    const [patientsError, setPatientsError] = useState("");
    const [patients, setPatients] = useState([]);
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [sidebarOpenDesktop, setSidebarOpenDesktop] = useState(true);
    const [search, setSearch] = useState(undefined);
    const [loading, setLoading] = useState(true);
    const [total, setTotal] = useState(0);
    const [cities, setCities] = useState([]);
    const [page, setPage] = useState(1);
    const [next, setNext] = useState(null);
    const [previous, setPrevious] = useState(null);
    const isDesktop = useMediaQuery("(min-width: 1024px)");

    useEffect(() => {
        if (isDesktop) {
            setSidebarOpen(false);
        }
    }, [isDesktop]);

    // getting initial data
    useEffect(() => {
        api.cities.getMulti().then((response) => {
            setCities(response);
            setLoading(false);
            // api.patients.getMulti('modification', search, null, null, null).then((resp) => (setPatients(resp.results.filter((array) => { return array.firstName ? true : false }).map((element) => {
            //     return ({ ...element, name: element.firstName + ' ' + element.lastName, city: response.find((city) => city.id === element.city)?.name, sex: element.sex === 1 ? 'Masculino' : 'Femenino' })
            // })), setTotal(resp.count), setLoading(false)))
        });
    }, []);

    useEffect(() => {
        setPage(1);
    }, [search]);

    useEffect(() => {
        if (!search) {
            if (search === "") {
                setPatients([]);
                setTotal(0);
                setNext(null);
                setPrevious(null);
            }
            return;
        }

        setLoading(true);

        api.patients
            .getMulti("alphabetic", search, null, null, { page })
            .then((response) => {
                setNext(response.next);
                setPrevious(response.previous);
                setTotal(response.count);

                setPatients(
                    response.results
                        .filter((p) => !!p.firstName)
                        .map((element) => ({
                            ...element,
                            name: `${element.firstName} ${element.lastName}`,
                            city: cities.find((c) => c.id === element.city)
                                ?.name,
                            sex: element.sex === 1 ? "Masculino" : "Femenino",
                            lastConsultationDate: element.lastConsultationDate
                                ? formatDateDDMMYYYY(element.lastConsultationDate)
                                : element.lastConsultationDate,
                        })),
                );
            })
            .catch((e) => {
                // si querés mostrar mensaje real, depende de la forma de e.message en tu api.js
                setPatientsError(e?.message || "Error al cargar pacientes");
            })
            .finally(() => setLoading(false));
    }, [search, page, cities]);

    const totalPages = Math.ceil(total / PAGE_SIZE);

    const start = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
    const end = Math.min(page * PAGE_SIZE, total);

    // const pages = Array.from({ length: totalPages }, (_, i) => ({
    //     number: i + 1,
    //     current: i + 1 === page,
    // }));

    return (
        <PatientModuleLayout
            sidebarOpen={sidebarOpen}
            setSidebarOpen={setSidebarOpen}
            sidebarOpenDesktop={sidebarOpenDesktop}
            currentScreen="Pacientes"
            loading={false}
            header={
                <LocalHeader
                    search={search}
                    setSearch={setSearch}
                    sidebarOpen={sidebarOpen}
                    setSidebarOpen={setSidebarOpen}
                    isDesktop={isDesktop}
                    sidebarOpenDesktop={sidebarOpenDesktop}
                    setSidebarOpenDesktop={setSidebarOpenDesktop}
                    showNewButton={patients.length > 0}
                />
            }
        >
            <div className="px-4 py-6 pb-24 sm:px-6 lg:px-8 lg:pb-8">
                {patientsError && (
                    <div className="mb-3 text-sm text-red-600">
                        {patientsError}
                    </div>
                )}
                {loading ? (
                    <div className="flex min-h-[60vh] items-center justify-center">
                        <div className="w-16 h-16 border-b-2 border-blue-900 dark:border-white rounded-full animate-spin"></div>
                    </div>
                ) : patients.length ? (
                    <>
                        <h2 className="text-2xl sm:text-4xl font-bold dark:text-gray-200 mb-6">
                            Pacientes
                        </h2>
                        <p className="text-sm font-semibold text-gray-600 dark:text-gray-400 mb-4">
                            Total de pacientes registrados: {total}
                        </p>

                        {/* Desktop table */}
                        <div className="hidden lg:block">
                            <Table
                                module="patients"
                                actiontext="Ver"
                                columns={columns}
                                rows={patients}
                            />
                        </div>

                        {/* Mobile card list */}
                        <div className="lg:hidden">
                            <PatientListMobile
                                patients={patients}
                                loading={false}
                            />
                        </div>

                        <PageControler
                            start={start}
                            end={end}
                            total={total}
                            page={page}
                            totalPages={totalPages}
                            setPage={setPage}
                            previous={previous}
                            next={next}
                        />
                    </>
                ) : (
                    <div className="flex min-h-[60vh]">
                        <div className="mx-auto my-auto text-center">
                            <NoSymbolIcon
                                className="h-12 w-12 mx-auto text-gray-400"
                                aria-hidden="true"
                            />
                            <h3 className="mt-2 text-sm font-semibold text-gray-800 dark:text-gray-200">
                                No se encontraron resultados
                            </h3>
                            <p className="mt-1 text-sm font-semibold text-gray-600 dark:text-gray-400">
                                Probá con otros datos o registrá un
                                nuevo paciente.
                            </p>
                            <div className="mt-6">
                                <Link
                                    to="/patients/new"
                                    type="button"
                                    className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-semibold rounded-md text-white bg-primary hover:bg-primary"
                                >
                                    <PlusIcon
                                        className="-ml-1 mr-2 h-5 w-5"
                                        aria-hidden="true"
                                    />
                                    Nuevo paciente
                                </Link>
                            </div>
                        </div>
                    </div>
                )}
            </div>
            {/* Floating action button – mobile only */}
            <Link
                to="/patients/new"
                className="fixed bottom-5 right-5 z-20 inline-flex items-center gap-2 rounded-full bg-primary px-5 py-3 text-sm font-semibold text-white shadow-lg transition-transform hover:scale-[1.02] lg:hidden"
            >
                <PlusIcon className="h-5 w-5" />
                <span>Nuevo paciente</span>
            </Link>
        </PatientModuleLayout>
    );
}

const SystemStatusIndicator = () => {
    const { systemStatus } = useSystemConfig();

    if (!systemStatus) return null;

    if (!systemStatus.systemEnabled) {
        return (
            <div className="flex items-center px-3 py-1 bg-red-100 dark:bg-red-900 rounded-md">
                <XCircleIcon className="h-5 w-5 text-red-600 dark:text-red-300 mr-2" />
                <span className="text-sm font-medium text-red-800 dark:text-red-200">
                    Sistema Deshabilitado
                </span>
            </div>
        );
    }

    if (!systemStatus.maxMonthlyConsultations) return null;

    const percentage =
        (systemStatus.currentMonthConsultations /
            systemStatus.maxMonthlyConsultations) *
        100;

    let usageLevel = null;

    if (percentage >= 96) {
        usageLevel = {
            bgClass: "bg-red-100 dark:bg-red-900",
            textClass: "text-red-800 dark:text-red-200",
            iconClass: "text-red-600 dark:text-red-300",
            icon: XCircleIcon,
            message: `Límite alcanzado (${systemStatus.currentMonthConsultations}/${systemStatus.maxMonthlyConsultations})`,
        };
    } else if (percentage >= 81) {
        usageLevel = {
            bgClass: "bg-orange-100 dark:bg-orange-900",
            textClass: "text-orange-800 dark:text-orange-200",
            iconClass: "text-orange-600 dark:text-orange-300",
            icon: ExclamationTriangleIcon,
            message: `Muy cerca del límite (${systemStatus.currentMonthConsultations}/${systemStatus.maxMonthlyConsultations})`,
        };
    } else if (percentage >= 61) {
        usageLevel = {
            bgClass: "bg-yellow-100 dark:bg-yellow-900",
            textClass: "text-yellow-800 dark:text-yellow-200",
            iconClass: "text-yellow-600 dark:text-yellow-300",
            icon: ExclamationTriangleIcon,
            message: `Acercándose al límite (${systemStatus.currentMonthConsultations}/${systemStatus.maxMonthlyConsultations})`,
        };
    } else if (percentage > 0) {
        usageLevel = {
            bgClass: "bg-green-100 dark:bg-green-900",
            textClass: "text-green-800 dark:text-green-200",
            iconClass: "text-green-600 dark:text-green-300",
            icon: CheckCircleIcon,
            message: `Consultas: ${systemStatus.currentMonthConsultations}/${systemStatus.maxMonthlyConsultations}`,
        };
    }

    if (!usageLevel) return null;

    const Icon = usageLevel.icon;

    return (
        <div
            className={`flex items-center px-3 py-1 ${usageLevel.bgClass} rounded-md`}
        >
            <Icon className={`h-5 w-5 ${usageLevel.iconClass} mr-2`} />
            <span className={`text-sm font-medium ${usageLevel.textClass}`}>
                {usageLevel.message}
            </span>
        </div>
    );
};

const LocalHeader = ({
    search,
    setSearch,
    setSidebarOpen,
    sidebarOpen,
    isDesktop,
    setSidebarOpenDesktop,
    sidebarOpenDesktop,
    showNewButton,
}) => {
    const [value, setValue] = useState(search);

    const handleKeyDown = (e) => {
        if (e.code === "Enter") {
            setSearch(e.target.value);
        }
    };

    const handleSidebarToggle = () => {
        if (isDesktop) {
            setSidebarOpenDesktop(!sidebarOpenDesktop);
            return;
        }
        setSidebarOpen(!sidebarOpen);
    };

    return (
        <div className="sticky top-0 z-20 border-b border-slate-200 bg-white/95 backdrop-blur dark:border-slate-800 dark:bg-slate-950/90">
            <div className="flex min-h-[72px] items-center justify-between gap-3 px-4 py-3 sm:px-6 lg:px-6">
                <div className="flex min-w-0 flex-1 items-center gap-3">
                    <SidebarToggleButton onClick={handleSidebarToggle} />
                    <div className="flex min-w-0 flex-1 items-center rounded-xl border border-slate-200 bg-slate-50 px-3 text-slate-700 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200">
                        <label htmlFor="patient-search">
                            <MagnifyingGlassIcon className="h-5 w-5 shrink-0 text-primary" />
                        </label>
                        <input
                            type="search"
                            name="patient-search"
                            id="patient-search"
                            value={value}
                            onChange={(e) => setValue(e.target.value)}
                            onKeyDown={handleKeyDown}
                            className="w-full border-none bg-transparent px-3 py-2.5 text-sm focus:border-transparent focus:outline-none focus:ring-0"
                            placeholder="Buscar por nombre o número de documento"
                        />
                    </div>
                </div>
                <div className="hidden lg:block">
                    <SystemStatusIndicator />
                </div>
                <div className="hidden lg:flex items-center justify-end gap-3">
                    {showNewButton && (
                        <Link
                            to="/patients/new"
                            className="items-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-sm font-semibold text-white shadow-sm inline-flex"
                        >
                            <PlusIcon className="h-5 w-5" />
                            <span>Nuevo paciente</span>
                        </Link>
                    )}
                </div>
            </div>
        </div>
    );
};
